import React, {useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';

import {type ReportMetadata} from '../../../util/gallery';
import {imgSrcWithDefault} from '../../../util/images';
import * as Urls from '../../../util/urls';
import * as S from './styles';

const BACKUP_IMAGE_URL =
  'https://site.wandb.ai/wp-content/uploads/2024/07/cropped-favicon-3.png';

export type FeaturedBannerProps = {
  reports: ReportMetadata[];
};

type BannerSlideProps = {
  report: ReportMetadata;
  active: boolean;
};

const BannerSlide: React.FC<BannerSlideProps> = ({report, active}) => {
  const {id, displayName, project, entityName, previewUrl} = report;

  const displayImage = previewUrl || BACKUP_IMAGE_URL;

  const link = project
    ? Urls.reportView({
        entityName: project.entityName,
        projectName: project.name,
        reportID: id,
        reportName: displayName,
      })
    : Urls.galleryPostView({
        entityName: entityName,
        reportID: id,
        reportName: displayName,
      });

  return (
    <S.Slide $active={active}>
      <S.Content>
        <S.LeftContent>
          <S.Title>{displayName}</S.Title>
          <S.ReadMoreButton as={Link} to={link}>
            <span>LEARN MORE</span>
            <svg
              width="13"
              height="13"
              viewBox="0 0 13 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{marginLeft: 8}}>
              <path
                d="M6.62891 1.78223L11.368 6.52136L6.62891 11.2605"
                stroke="currentColor"
                strokeWidth="1.3443"
                strokeLinecap="square"
              />
              <path
                d="M10.9756 6.52148L1.28613 6.52148"
                stroke="currentColor"
                strokeWidth="1.3443"
                strokeLinecap="square"
              />
            </svg>
          </S.ReadMoreButton>
        </S.LeftContent>
        <S.RightContent>
          <S.FeaturedImage
            src={imgSrcWithDefault(displayImage)}
            alt={displayName}
          />
        </S.RightContent>
      </S.Content>
    </S.Slide>
  );
};

export const FeaturedBanner: React.FC<FeaturedBannerProps> = ({reports}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const validReports = useMemo(() => {
    return reports.slice(0, 5);
  }, [reports]);

  useEffect(() => {
    if (validReports.length <= 1 || isPaused) {
      return;
    }

    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % validReports.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [validReports.length, isPaused]);

  const handleDotClick = (index: number) => {
    setActiveIndex(index);
    setIsPaused(true);
  };

  if (validReports.length === 0) {
    return null;
  }

  return (
    <S.BannerContainer
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}>
      {validReports.map((report, index) => (
        <BannerSlide
          key={report.id}
          report={report}
          active={index === activeIndex}
        />
      ))}

      {validReports.length > 1 && (
        <S.DotsContainer>
          {validReports.map((_, index) => (
            <S.Dot
              // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
              key={index}
              $active={index === activeIndex}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </S.DotsContainer>
      )}
    </S.BannerContainer>
  );
};
